import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableColumn,
  Progress,
  ResponseErrorPanel,
  InfoCard,
  StatusOK,
  StatusError,
  StatusWarning,
  StatusAborted,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import {
  Grid,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  makeStyles,
  Button,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import StorageIcon from '@material-ui/icons/Storage';
import CloudIcon from '@material-ui/icons/Cloud';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import TimerIcon from '@material-ui/icons/Timer';
import BuildIcon from '@material-ui/icons/Build';
import PublishIcon from '@material-ui/icons/Publish';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReplayIcon from '@material-ui/icons/Replay';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import LaunchIcon from '@material-ui/icons/Launch';
import { githubWorkflowsApiRef } from '@veecode-platform/github-workflows-common';

// --- Types ---

interface Environment {
  id: string;
  name: string;
  description: string;
  accountId: string;
  region: string;
  isDefault: boolean;
}

interface EnvSummary {
  envId: string;
  rds: { clusterCount: number; error: string | null };
  eks: { clusterCount: number; error: string | null };
}

interface KnownDatabase {
  clusterIdentifier: string;
  databaseName: string;
  engine: string;
  engineVersion: string;
  status: string;
  port: number;
  region: string;
  appCode: string;
  env: string;
  proxyPort: number;
  tunnelStatus?: string;
  tunnelError?: string | null;
}

interface Credentials {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  expiresAt: string;
  clusterIdentifier: string;
  appCode: string;
  rdsEndpoint?: string;
  tunnelStatus?: string;
}

interface WorkflowItem {
  workflow: { id: number; name: string; state: string; url: string; path: string; createdAt: string; updatedAt: string };
  latestRun: { id?: number; status?: string; conclusion?: string };
  parameters: Array<{ name: string; description: string; default: string; required: boolean; type: string; options?: string[] }>;
}

interface PodItem {
  name: string;
  namespace: string;
  status: string;
  ready: string;
  restarts: number;
  startTime: string | null;
  ageMs: number | null;
  ownerKind: string | null;
  ownerName: string | null;
  deploymentName: string | null;
}

interface BuildNotification {
  phase: 'polling' | 'running' | 'done';
  runId: number | null;
  status: string | null;
  conclusion: string | null;
  htmlUrl: string | null;
  actor: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  dispatchedAt: number;
}

// --- Workflow Quick Action Constants ---
const NEXGEN_REPO = 'exo-imaging/cloud-build-nexgen-infra';
const NEXGEN_HOSTNAME = 'github.com';
const BUILD_WORKFLOW_MATCH = 'build all';
const DEPLOY_WORKFLOW_MATCH = 'deploy all';
const ENV_ID_TO_DEPLOY_ENV: Record<string, string> = { dev: 'dev', qa: 'qa', uat: 'uat', sandbox: 'sandbox' };

// --- Styles ---

const useStyles = makeStyles(theme => ({
  // Summary cards
  summaryCard: { height: '100%' },
  metricBox: { textAlign: 'center', padding: theme.spacing(2) },
  metricValue: { fontSize: '2rem', fontWeight: 'bold', color: theme.palette.primary.main },
  metricLabel: { color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  metricIcon: { fontSize: '1.5rem', color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) },
  // Environment section
  envSection: { marginBottom: theme.spacing(2) },
  envDetails: { display: 'block', padding: theme.spacing(2) },
  sectionTitle: { marginTop: theme.spacing(3), marginBottom: theme.spacing(1), display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  sectionIcon: { color: theme.palette.text.secondary },
  // Cluster accordions
  clusterAccordion: { border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } },
  clusterDetails: { display: 'block', padding: theme.spacing(2) },
  clusterSummaryContent: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' as const, width: '100%' },
  clusterMetric: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  // Connectivity / detail info
  label: { fontWeight: 'bold', marginRight: theme.spacing(1) },
  mono: { fontFamily: 'monospace', fontSize: '0.85rem' },
  detailRow: { marginBottom: theme.spacing(0.5) },
  // Status chips
  defaultChip: { backgroundColor: theme.palette.primary.main, color: '#fff', marginLeft: theme.spacing(1) },
  // Tunnel status
  tunnelConnected: { backgroundColor: '#10b981', color: '#fff' },
  tunnelDisconnected: { backgroundColor: '#ef4444', color: '#fff' },
  tunnelInitializing: { backgroundColor: '#f59e0b', color: '#fff' },
  // App code chips
  pulseChip: { backgroundColor: '#6366f1', color: '#fff' },
  essentialChip: { backgroundColor: '#0ea5e9', color: '#fff' },
  // Credentials panel
  credentialsCard: { marginBottom: theme.spacing(2), border: `2px solid ${theme.palette.success?.main || '#4caf50'}` },
  credentialField: { display: 'flex', alignItems: 'center', marginBottom: theme.spacing(1) },
  credentialLabel: { fontWeight: 'bold', minWidth: 100, marginRight: theme.spacing(2) },
  credentialValue: {
    fontFamily: 'monospace', backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0.5, 1), borderRadius: theme.shape.borderRadius,
    flexGrow: 1, wordBreak: 'break-all' as const,
  },
  expiryBanner: {
    marginTop: theme.spacing(1), padding: theme.spacing(1, 2),
    backgroundColor: 'rgba(255, 152, 0, 0.15)', borderRadius: theme.shape.borderRadius,
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
  },
  actionButtons: { display: 'flex', gap: theme.spacing(1), marginTop: theme.spacing(2) },
  errorText: { color: theme.palette.error.main, fontStyle: 'italic' },
  requestBtn: { textTransform: 'none' as const },
  nodeGroupChip: { margin: theme.spacing(0.5) },
  // Workflow quick actions
  workflowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  workflowActionsLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    fontSize: '0.85rem',
  },
  buildBtn: {
    textTransform: 'none' as const,
    borderColor: '#7df3e1',
    color: '#7df3e1',
    '&:hover': { borderColor: '#7df3e1', backgroundColor: 'rgba(125, 243, 225, 0.08)' },
  },
  deployBtn: {
    textTransform: 'none' as const,
    borderColor: '#6366f1',
    color: '#6366f1',
    '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.08)' },
  },
  paramField: { marginBottom: theme.spacing(1.5), width: '100%' },
  paramSectionLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  // Pod listing
  podSection: { marginTop: theme.spacing(3) },
  podSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  podTableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  podTableHead: { backgroundColor: 'rgba(255,255,255,0.04)' },
  podTableHeadCell: {
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  podTableCell: {
    fontSize: '0.82rem',
    padding: '6px 12px',
    verticalAlign: 'middle' as const,
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
  },
  deploymentRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  restartBtn: {
    textTransform: 'none' as const,
    fontSize: '0.78rem',
    borderColor: 'rgba(249,115,22,0.4)',
    color: '#f97316',
    '&:hover': { borderColor: '#f97316', backgroundColor: 'rgba(249,115,22,0.08)' },
  },
  // Database dump panel
  dumpPanel: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  dumpRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    marginTop: theme.spacing(2),
  },
  dumpBtn: {
    textTransform: 'none' as const,
    minWidth: 140,
    height: 40,
    flexShrink: 0,
  },
  // Build completion popup
  buildPopup: {
    position: 'fixed' as const,
    bottom: 24,
    right: 24,
    zIndex: 1400,
    minWidth: 380,
    maxWidth: 460,
    backgroundColor: '#111827',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    padding: theme.spacing(2, 2.5),
  },
  buildPopupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  buildPopupTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buildPopupDivider: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  buildPopupLink: {
    color: '#818cf8',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  },
  buildPopupGhLink: {
    color: '#7df3e1',
    textDecoration: 'none',
    fontSize: '0.82rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    '&:hover': { textDecoration: 'underline' },
  },
}));

// --- Helper Components ---

const RdsStatusChip = ({ status }: { status: string }) => {
  switch (status?.toLowerCase()) {
    case 'available': return <StatusOK>Available</StatusOK>;
    case 'modifying': case 'configuring-iam-database-auth': return <StatusWarning>Modifying</StatusWarning>;
    case 'creating': return <StatusAborted>Creating</StatusAborted>;
    case 'deleting': return <StatusError>Deleting</StatusError>;
    default: return <Chip label={status || 'Unknown'} size="small" />;
  }
};

const EksStatusChip = ({ status }: { status: string }) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE': return <StatusOK>Active</StatusOK>;
    case 'CREATING': return <StatusAborted>Creating</StatusAborted>;
    case 'UPDATING': return <StatusWarning>Updating</StatusWarning>;
    case 'DELETING': case 'FAILED': return <StatusError>{status}</StatusError>;
    default: return <Chip label={status || 'Unknown'} size="small" />;
  }
};

const TunnelChip = ({ status, classes }: { status: string; classes: any }) => {
  const chipClass = status === 'connected'
    ? classes.tunnelConnected
    : status === 'failed' || status === 'disconnected'
      ? classes.tunnelDisconnected
      : classes.tunnelInitializing;
  return <Chip label={status || 'N/A'} size="small" className={chipClass} />;
};

// --- Pod Helpers ---

function formatAge(ageMs: number | null) {
  if (ageMs === null) return '—';
  const secs = Math.floor(ageMs / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatDuration(ms: number): string {
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  if (mins < 60) return remSecs > 0 ? `${mins}m ${remSecs}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

function formatRelativeTime(timestampMs: number): string {
  const diffMs = Date.now() - timestampMs;
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const PodStatusChip = ({ status }: { status: string }) => {
  switch (status) {
    case 'Running': return <StatusOK>Running</StatusOK>;
    case 'Pending': return <StatusWarning>Pending</StatusWarning>;
    case 'Succeeded': return <StatusOK>Completed</StatusOK>;
    case 'Failed': return <StatusError>Failed</StatusError>;
    default: return <Chip label={status || 'Unknown'} size="small" />;
  }
};

// --- EKS Cluster Panel (manages its own pod state) ---

const DUMP_CLUSTERS = [
  { value: 'rds-dev-us-essential', label: 'dev — Essential (EW)' },
  { value: 'rds-dev-us-pulse',     label: 'dev — Pulse' },
  { value: 'rds-qa-us-essential',  label: 'qa — Essential (EW)' },
  { value: 'rds-qa-us-pulse',      label: 'qa — Pulse' },
  { value: 'rds-uat-us-essential', label: 'uat — Essential (EW)' },
  { value: 'rds-uat-us-pulse',     label: 'uat — Pulse' },
];

const DatabaseDumpPanel = ({
  baseUrl,
  fetchApi,
}: {
  baseUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [cluster, setCluster] = useState('');
  const [dbName, setDbName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDump = async () => {
    if (!cluster || !dbName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchApi.fetch(`${baseUrl}/db/dump`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clusterIdentifier: cluster, targetDatabase: dbName.trim() }),
      });

      if (!response.ok) {
        let errorMsg = `Server error (HTTP ${response.status})`;
        try {
          const data = await response.json();
          errorMsg = data.error || errorMsg;
        } catch {
          // Non-JSON error (e.g. nginx 504 timeout page) — show status code only
        }
        setError(errorMsg);
        return;
      }

      // Trigger browser download from the streamed response
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${dbName.trim()}.dump`;

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.dumpPanel}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        Database Dump
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
        Export a single logical database as a compressed <code>.dump</code> file (pg_dump custom format).
        Restore with: <code>pg_restore -d &lt;target_db&gt; file.dump</code>
      </Typography>

      <Box className={classes.dumpRow}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel>RDS Connection</InputLabel>
          <Select
            value={cluster}
            onChange={e => setCluster(e.target.value as string)}
            label="RDS Connection"
          >
            {DUMP_CLUSTERS.map(c => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          size="small"
          label="Database name"
          placeholder="e.g. 0B703C11BF2C4564"
          value={dbName}
          onChange={e => setDbName(e.target.value)}
          style={{ minWidth: 260 }}
          onKeyDown={e => { if (e.key === 'Enter') handleDump(); }}
        />

        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.dumpBtn}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <GetAppIcon />}
          onClick={handleDump}
          disabled={loading || !cluster || !dbName.trim()}
        >
          {loading ? 'Dumping…' : 'Request Dump'}
        </Button>
      </Box>

      {error && (
        <Box mt={1.5}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
    </Box>
  );
};

const EKS_ALLOWED_DEPLOYMENTS: Record<string, string[]> = {
  essential: [
    'cloud-hcs',
    'dicom-extractor',
    'dicom-image-processor',
    'dicom-receiver',
    'exam-results-reporter',
    'pacs-push',
    'patient-data-reconciler',
    'smartlink',
  ],
  pulse: [
    'auth-service-deployment',
    'device-management-deployment',
    'organization-management-deployment',
  ],
};

const getFilteredDeployments = (
  clusterName: string,
  deployments: { name: string; namespace: string }[],
) => {
  const key = Object.keys(EKS_ALLOWED_DEPLOYMENTS).find(k =>
    clusterName.toLowerCase().includes(k),
  );
  if (!key) return deployments;
  const allowed = EKS_ALLOWED_DEPLOYMENTS[key];
  return deployments.filter(d => allowed.includes(d.name));
};

const EksClusterPanel = ({
  cluster,
  envId,
  baseUrl,
  fetchApi,
  onSnackbar,
}: {
  cluster: any;
  envId: string;
  baseUrl: string;
  fetchApi: any;
  onSnackbar: (msg: string, severity: 'success' | 'error') => void;
}) => {
  const classes = useStyles();
  const [pods, setPods] = useState<PodItem[]>([]);
  const [deployments, setDeployments] = useState<{ name: string; namespace: string }[]>([]);
  const [loadingPods, setLoadingPods] = useState(false);
  const [podError, setPodError] = useState<string | null>(null);
  const [podsLoaded, setPodsLoaded] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState<{ name: string; namespace: string } | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [nsFilter, setNsFilter] = useState<string[]>(['dev', 'qa', 'uat']);

  const loadPods = async () => {
    setLoadingPods(true);
    setPodError(null);
    try {
      const res = await fetchApi.fetch(
        `${baseUrl}/envs/${envId}/eks/cluster/${encodeURIComponent(cluster.name)}/pods`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPods(data.pods || []);
      setDeployments(data.deployments || []);
      setPodsLoaded(true);
    } catch (e: any) {
      setPodError(e.message);
    } finally {
      setLoadingPods(false);
    }
  };

  useEffect(() => { loadPods(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRestartConfirm = async () => {
    if (!confirmRestart) return;
    setRestarting(true);
    try {
      const res = await fetchApi.fetch(
        `${baseUrl}/envs/${envId}/eks/cluster/${encodeURIComponent(cluster.name)}/deployments/restart`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ namespace: confirmRestart.namespace, deploymentName: confirmRestart.name }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onSnackbar(`Restart initiated for ${confirmRestart.namespace}/${confirmRestart.name}`, 'success');
    } catch (e: any) {
      onSnackbar(`Restart failed: ${e.message}`, 'error');
    } finally {
      setRestarting(false);
      setConfirmRestart(null);
    }
  };

  const uniqueNamespaces = [...new Set(pods.map(p => p.namespace))].sort();
  const visiblePods = pods.filter(p => nsFilter.length === 0 || nsFilter.includes(p.namespace));
  const toggleNs = (ns: string) =>
    setNsFilter(prev => prev.includes(ns) ? prev.filter(n => n !== ns) : [...prev, ns]);

  return (
    <>
      {/* Cluster info grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>Cluster Info</Typography>
          <Box className={classes.detailRow}><span className={classes.label}>Platform:</span> {cluster.platformVersion}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Role:</span> {cluster.roleArn?.split('/').pop() || 'N/A'}</Box>
          <Box className={classes.detailRow}>
            <span className={classes.label}>Endpoint:</span>
            <Typography variant="body2" className={classes.mono} style={{ wordBreak: 'break-all' }}>
              {cluster.endpoint}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>Networking</Typography>
          <Box className={classes.detailRow}><span className={classes.label}>VPC:</span> {cluster.resourcesVpcConfig?.vpcId || 'N/A'}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Subnets:</span> {cluster.resourcesVpcConfig?.subnetIds?.length || 0}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Security Groups:</span> {cluster.resourcesVpcConfig?.securityGroupIds?.length || 0}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Public Access:</span> {cluster.resourcesVpcConfig?.endpointConfigResponse?.publicAccess ? 'Yes' : 'No'}</Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>Compute</Typography>
          <Box className={classes.detailRow}><span className={classes.label}>Current Nodes:</span> {cluster.totalNodes ?? 0}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Desired:</span> {cluster.totalDesiredNodes ?? 0}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Min:</span> {cluster.totalMinNodes ?? 0}</Box>
          <Box className={classes.detailRow}><span className={classes.label}>Max:</span> {cluster.totalMaxNodes ?? 0}</Box>
        </Grid>
      </Grid>

      {/* Node Groups */}
      {cluster.nodeGroups && cluster.nodeGroups.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>Node Groups</Typography>
          {cluster.nodeGroups.map((ng: any) => (
            <Chip
              key={ng.nodegroupName}
              label={`${ng.nodegroupName}: ${ng.scalingConfig?.desiredSize || 0} nodes (${ng.instanceTypes?.join(', ') || '?'}) - ${ng.capacityType || 'N/A'}`}
              size="small"
              className={classes.nodeGroupChip}
              color={ng.status === 'ACTIVE' ? 'primary' : 'default'}
            />
          ))}
        </Box>
      )}

      {/* Pods Section */}
      <Box className={classes.podSection}>
        <Box className={classes.podSectionHeader}>
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">
              Pods{podsLoaded ? ` (${visiblePods.length}${visiblePods.length !== pods.length ? `/${pods.length}` : ''})` : ''}
            </Typography>
            {podsLoaded && uniqueNamespaces.map(ns => (
              <Chip
                key={ns}
                label={ns}
                size="small"
                clickable
                color={nsFilter.includes(ns) ? 'primary' : 'default'}
                onClick={() => toggleNs(ns)}
                style={{ fontSize: '0.7rem', height: 20 }}
              />
            ))}
            {podsLoaded && nsFilter.length > 0 && (
              <Chip
                label="Show All"
                size="small"
                clickable
                onClick={() => setNsFilter([])}
                style={{ fontSize: '0.7rem', height: 20, opacity: 0.6 }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={loadPods} disabled={loadingPods} title="Refresh pods">
            {loadingPods ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>

        {podError && (
          <Alert severity="error" style={{ marginBottom: 8 }}>{podError}</Alert>
        )}

        {/* Deployment restart buttons */}
        {deployments.length > 0 && (
          <Box className={classes.deploymentRow}>
            <Typography style={{ fontSize: '0.78rem', color: '#6b7280', alignSelf: 'center' }}>
              Restart:
            </Typography>
            {getFilteredDeployments(cluster.name, deployments).map(dep => (
              <Button
                key={`${dep.namespace}/${dep.name}`}
                variant="outlined"
                size="small"
                className={classes.restartBtn}
                startIcon={<ReplayIcon style={{ fontSize: 14 }} />}
                onClick={() => setConfirmRestart(dep)}
              >
                {dep.name}
              </Button>
            ))}
          </Box>
        )}

        {/* Pod table */}
        {loadingPods && !podsLoaded && (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">Loading pods...</Typography>
          </Box>
        )}
        {podsLoaded && (
          <Box className={classes.podTableContainer}>
            <MuiTable size="small">
              <TableHead className={classes.podTableHead}>
                <TableRow>
                  <TableCell className={classes.podTableHeadCell}>Name</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Namespace</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Status</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Ready</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Restarts</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Age</TableCell>
                  <TableCell className={classes.podTableHeadCell}>Owner</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiblePods.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: 16 }}>
                      {pods.length === 0 ? 'No pods found' : 'No pods match the selected namespace filter'}
                    </TableCell>
                  </TableRow>
                )}
                {visiblePods.map(pod => (
                  <TableRow key={`${pod.namespace}/${pod.name}`} hover>
                    <TableCell className={classes.podTableCell}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{pod.name}</span>
                    </TableCell>
                    <TableCell className={classes.podTableCell}>{pod.namespace}</TableCell>
                    <TableCell className={classes.podTableCell}>
                      <PodStatusChip status={pod.status} />
                    </TableCell>
                    <TableCell className={classes.podTableCell}>{pod.ready}</TableCell>
                    <TableCell className={classes.podTableCell}>
                      {pod.restarts > 0
                        ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{pod.restarts}</span>
                        : pod.restarts}
                    </TableCell>
                    <TableCell className={classes.podTableCell}>{formatAge(pod.ageMs)}</TableCell>
                    <TableCell className={classes.podTableCell}>
                      {pod.ownerName
                        ? <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{pod.ownerName}</span>
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </Box>
        )}
      </Box>

      {/* Restart Confirmation Dialog */}
      <Dialog open={!!confirmRestart} onClose={() => setConfirmRestart(null)}>
        <DialogTitle>Restart Deployment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Perform a rolling restart of <strong>{confirmRestart?.name}</strong> in namespace{' '}
            <strong>{confirmRestart?.namespace}</strong>?
          </DialogContentText>
          <DialogContentText>
            All pods will be replaced gradually — zero downtime.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestart(null)} disabled={restarting}>Cancel</Button>
          <Button
            onClick={handleRestartConfirm}
            color="primary"
            variant="contained"
            disabled={restarting}
            startIcon={restarting ? <CircularProgress size={16} /> : undefined}
          >
            {restarting ? 'Restarting...' : 'Restart'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Credentials Panel ---

const CredentialsPanel = ({
  credentials,
  classes,
  fetchApi,
  baseUrl,
  onSnackbar,
}: {
  credentials: Credentials;
  classes: any;
  fetchApi: any;
  baseUrl: string;
  onSnackbar: (msg: string) => void;
}) => {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    onSnackbar(`${label} copied to clipboard`);
  };

  const copyConnectionString = () => {
    const connStr = `postgresql://${credentials.username}:${credentials.password}@${credentials.host}:${credentials.port}/${credentials.database}?sslmode=require`;
    copyToClipboard(connStr, 'Connection string');
  };

  const handleDownloadDBeaver = async () => {
    try {
      const response = await fetchApi.fetch(`${baseUrl}/db/generate-dbeaver-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dbeaver-connection-${credentials.database}-${credentials.username}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      onSnackbar('Failed to download DBeaver config');
    }
  };

  const expiry = new Date(credentials.expiresAt);
  const hoursLeft = Math.max(0, Math.round((expiry.getTime() - Date.now()) / (1000 * 60 * 60)));

  return (
    <InfoCard
      title="Temporary Credentials"
      subheader={`Database: ${credentials.database} | Cluster: ${credentials.clusterIdentifier}`}
      className={classes.credentialsCard}
    >
      <Box className={classes.expiryBanner}>
        <TimerIcon />
        <Typography variant="body2">
          <strong>Expires:</strong> {expiry.toLocaleString()} ({hoursLeft}h remaining)
        </Typography>
      </Box>
      <Box mt={2}>
        {[
          { label: 'Host', value: credentials.host },
          { label: 'Port', value: String(credentials.port) },
          { label: 'Database', value: credentials.database },
          { label: 'Username', value: credentials.username },
          { label: 'Password', value: credentials.password },
        ].map(({ label, value }) => (
          <Box key={label} className={classes.credentialField}>
            <Typography className={classes.credentialLabel}>{label}:</Typography>
            <Typography className={classes.credentialValue}>{value}</Typography>
            <IconButton size="small" onClick={() => copyToClipboard(value, label)} title={`Copy ${label}`}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box className={classes.actionButtons}>
        <Button variant="outlined" color="primary" startIcon={<FileCopyIcon />} onClick={copyConnectionString}>
          Copy Connection String
        </Button>
        <Button variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={handleDownloadDBeaver}>
          Download DBeaver Config
        </Button>
      </Box>
    </InfoCard>
  );
};

// --- Build Completion Popup ---

const BuildCompletionPopup = ({
  notification,
  onClose,
}: {
  notification: BuildNotification;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const { phase, status, conclusion, htmlUrl, actor, createdAt, updatedAt } = notification;

  const durationMs =
    createdAt && updatedAt && status === 'completed'
      ? new Date(updatedAt).getTime() - new Date(createdAt).getTime()
      : null;

  const isSuccess = conclusion === 'success';
  const isFailure = conclusion === 'failure';
  const isComplete = phase === 'done';

  let statusLabel: string;
  let statusColor: string;
  let StatusIconEl: React.ReactNode = null;

  if (phase === 'polling') {
    statusLabel = 'Starting…';
    statusColor = '#f59e0b';
  } else if (status === 'queued') {
    statusLabel = 'Queued';
    statusColor = '#6b7280';
  } else if (status === 'in_progress') {
    statusLabel = 'In Progress';
    statusColor = '#f59e0b';
  } else if (isSuccess) {
    statusLabel = 'Succeeded';
    statusColor = '#22c55e';
    StatusIconEl = <CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />;
  } else if (isFailure) {
    statusLabel = 'Failed';
    statusColor = '#ef4444';
    StatusIconEl = <ErrorIcon style={{ fontSize: 14, color: '#ef4444' }} />;
  } else if (conclusion === 'cancelled') {
    statusLabel = 'Cancelled';
    statusColor = '#9ca3af';
  } else {
    statusLabel = status || 'Unknown';
    statusColor = '#6b7280';
  }

  return (
    <Paper elevation={10} className={classes.buildPopup}>
      {/* Header */}
      <Box className={classes.buildPopupHeader}>
        <Typography className={classes.buildPopupTitle}>
          <BuildIcon style={{ fontSize: '1rem', color: '#7df3e1' }} />
          Build All NexGen Services
        </Typography>
        <IconButton size="small" onClick={onClose} style={{ marginTop: -4, marginRight: -8, color: '#6b7280' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Status */}
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
        <Chip
          label={statusLabel}
          size="small"
          icon={StatusIconEl as any}
          style={{ backgroundColor: statusColor, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
        />
        {!isComplete && (
          <CircularProgress size={12} style={{ color: statusColor }} />
        )}
      </Box>

      {/* Details */}
      <Box style={{ fontSize: '0.82rem', lineHeight: 2 }}>
        {actor && (
          <Box display="flex" style={{ gap: 8 }}>
            <span style={{ color: '#9ca3af', minWidth: 90 }}>Triggered by</span>
            <span style={{ color: '#d1d5db', fontWeight: 500 }}>{actor}</span>
          </Box>
        )}
        {createdAt && (
          <Box display="flex" style={{ gap: 8 }}>
            <span style={{ color: '#9ca3af', minWidth: 90 }}>Started</span>
            <span style={{ color: '#d1d5db' }}>{formatRelativeTime(new Date(createdAt).getTime())}</span>
          </Box>
        )}
        {durationMs !== null && durationMs > 0 && (
          <Box display="flex" style={{ gap: 8 }}>
            <span style={{ color: '#9ca3af', minWidth: 90 }}>Duration</span>
            <span style={{ color: '#d1d5db' }}>{formatDuration(durationMs)}</span>
          </Box>
        )}
        {htmlUrl && (
          <Box display="flex" style={{ gap: 8 }}>
            <span style={{ color: '#9ca3af', minWidth: 90 }}>GitHub</span>
            <a href={htmlUrl} target="_blank" rel="noopener noreferrer" className={classes.buildPopupGhLink}>
              View run <LaunchIcon style={{ fontSize: 12 }} />
            </a>
          </Box>
        )}
      </Box>

      {/* Footer link */}
      <Box className={classes.buildPopupDivider}>
        <a href="/aws?tab=release-history" className={classes.buildPopupLink}>
          View in Build History →
        </a>
      </Box>
    </Paper>
  );
};

// --- Workflow Dispatch Dialog ---

const WorkflowDispatchDialog = ({
  open,
  onClose,
  title,
  workflow,
  defaultBranch,
  envId,
  workflowsApi,
  onSnackbar,
  onDispatchSuccess,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  workflow: WorkflowItem | null;
  defaultBranch: string;
  envId: string;
  workflowsApi: any;
  onSnackbar: (msg: string, severity: 'success' | 'error') => void;
  onDispatchSuccess?: () => void;
}) => {
  const classes = useStyles();
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [dispatching, setDispatching] = useState(false);

  useEffect(() => {
    if (!open || !workflow) return;
    const initial: Record<string, string> = {};
    workflow.parameters.forEach(p => {
      // Default all boolean deploy flags to true (recommended by Julian)
      if (p.name.includes('_deploy') && !p.name.includes('_env') && (p.default === 'false' || p.default === '')) {
        initial[p.name] = 'true';
      } else {
        initial[p.name] = p.default || '';
      }
    });
    // Pre-select environment for deploy workflows
    const deployEnv = ENV_ID_TO_DEPLOY_ENV[envId];
    if (deployEnv && initial.manual_deployment_env !== undefined) {
      initial.manual_deployment_env = deployEnv;
    }
    setFormValues(initial);
    setDispatching(false);
  }, [open, workflow, envId]);

  const handleDispatch = async () => {
    if (!workflow) return;
    setDispatching(true);
    try {
      await workflowsApi.startWorkflowRun(
        NEXGEN_HOSTNAME,
        NEXGEN_REPO,
        workflow.workflow.id,
        defaultBranch,
        formValues,
      );
      onSnackbar('Workflow dispatched successfully!', 'success');
      onDispatchSuccess?.();
      onClose();
    } catch (e: any) {
      onSnackbar(`Dispatch failed: ${e.message}`, 'error');
    } finally {
      setDispatching(false);
    }
  };

  if (!workflow) return null;

  // Group parameters for visual clarity
  // Note: env params (target_environment / manual_deployment_env) contain '_env' — excluded from deployParams explicitly
  const envParams = workflow.parameters.filter(p => p.name.includes('_env'));
  const tagParams = workflow.parameters.filter(p => p.name.includes('image_tag'));
  const deployParams = workflow.parameters.filter(p => p.name.includes('_deploy') && !p.name.includes('_env'));
  const otherParams = workflow.parameters.filter(
    p => !p.name.includes('_env') && !p.name.includes('image_tag') && !p.name.includes('_deploy'),
  );

  const cleanLabel = (raw: string) =>
    raw.replace(/^manual\s+(deployment\s+(to\s+)?)?/i, '').replace(/^\w/, c => c.toUpperCase()) || raw;

  const renderParam = (p: WorkflowItem['parameters'][0]) => {
    const value = formValues[p.name] ?? p.default ?? '';
    const label = cleanLabel(p.description || p.name);
    if (p.type === 'choice' && p.options && p.options.length > 0) {
      return (
        <FormControl key={p.name} className={classes.paramField} variant="outlined" size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={value}
            onChange={e => setFormValues(prev => ({ ...prev, [p.name]: e.target.value as string }))}
            label={label}
          >
            {p.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        key={p.name}
        className={classes.paramField}
        label={label}
        variant="outlined"
        size="small"
        value={value}
        onChange={e => setFormValues(prev => ({ ...prev, [p.name]: e.target.value }))}
      />
    );
  };

  const renderSection = (label: string, params: typeof workflow.parameters) => {
    if (params.length === 0) return null;
    return (
      <>
        <Typography className={classes.paramSectionLabel}>{label}</Typography>
        {params.map(renderParam)}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Dispatch <strong>{workflow.workflow.name}</strong> on branch <code>{defaultBranch}</code>
        </DialogContentText>
        {renderSection('Environment', envParams)}
        {renderSection('Image Tags', tagParams)}
        {renderSection('Deployment Flags', deployParams)}
        {renderSection('Parameters', otherParams)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={dispatching}>Cancel</Button>
        <Button
          onClick={handleDispatch}
          color="primary"
          variant="contained"
          disabled={dispatching}
          startIcon={dispatching ? <CircularProgress size={16} /> : undefined}
        >
          {dispatching ? 'Dispatching...' : workflow.workflow.name}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Environment Section ---

const EnvironmentSection = ({
  env,
  summary,
  knownDatabases,
  baseUrl,
  fetchApi,
  identityApi,
  buildWorkflow,
  deployWorkflow,
  workflowDefaultBranch,
  workflowsApi,
  workflowsLoading,
}: {
  env: Environment;
  summary: EnvSummary | undefined;
  knownDatabases: KnownDatabase[];
  baseUrl: string;
  fetchApi: any;
  identityApi: any;
  buildWorkflow: WorkflowItem | null;
  deployWorkflow: WorkflowItem | null;
  workflowDefaultBranch: string;
  workflowsApi: any;
  workflowsLoading: boolean;
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(env.isDefault);

  // Data state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [rdsClusters, setRdsClusters] = useState<any[] | null>(null);
  const [rdsInstances, setRdsInstances] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<Record<string, any>>({});
  const [eksClusters, setEksClusters] = useState<any[] | null>(null);

  // DB access state
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [confirmDb, setConfirmDb] = useState<KnownDatabase | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Workflow dispatch dialog
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [activeWorkflowAction, setActiveWorkflowAction] = useState<'build' | 'deploy' | null>(null);

  // Filter known databases for this environment
  const envDatabases = knownDatabases.filter(db => db.env === env.id);

  // Load data when expanded
  useEffect(() => {
    if (!expanded || rdsClusters !== null) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [rdsRes, instancesRes, eksRes] = await Promise.all([
          fetchApi.fetch(`${baseUrl}/envs/${env.id}/rds/clusters`).then((r: Response) => r.ok ? r.json() : []).catch(() => []),
          fetchApi.fetch(`${baseUrl}/envs/${env.id}/rds/cluster/_/instances`).then((r: Response) => r.ok ? r.json() : []).catch(() => []),
          fetchApi.fetch(`${baseUrl}/envs/${env.id}/eks/clusters`).then((r: Response) => r.ok ? r.json() : []).catch(() => []),
        ]);

        setRdsClusters(rdsRes);
        setRdsInstances(instancesRes);
        setEksClusters(eksRes);

        // Fetch metrics for each RDS cluster
        const metricsMap: Record<string, any> = {};
        await Promise.all(
          rdsRes.map(async (c: any) => {
            try {
              const res = await fetchApi.fetch(`${baseUrl}/envs/${env.id}/rds/cluster/${c.DBClusterIdentifier}/metrics`);
              if (res.ok) metricsMap[c.DBClusterIdentifier] = await res.json();
            } catch { /* ignore metric failures */ }
          }),
        );
        setMetrics(metricsMap);
      } catch (e: any) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [expanded, env.id, baseUrl, fetchApi, rdsClusters]);

  // Request database access
  const handleRequestAccess = async () => {
    if (!confirmDb) return;
    setRequesting(true);
    setAccessError(null);
    try {
      const identity = await identityApi.getBackstageIdentity();
      // userEntityRef is like "user:default/ronit-gandhi" — extract the name after "/"
      const backstageUser = identity.userEntityRef.split('/').pop() ?? 'unknown';

      const response = await fetchApi.fetch(`${baseUrl}/db/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clusterIdentifier: confirmDb.clusterIdentifier,
          databaseName: confirmDb.databaseName,
          appCode: confirmDb.appCode,
          backstageUser,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to create credentials');
      setCredentials(data.credentials);
    } catch (e: any) {
      setAccessError(e.message);
    } finally {
      setRequesting(false);
      setConfirmDb(null);
    }
  };

  const hasError = summary?.rds?.error || summary?.eks?.error;
  const rdsCount = summary?.rds?.clusterCount ?? '—';
  const eksCount = summary?.eks?.clusterCount ?? '—';

  // RDS instance columns
  const instanceColumns: TableColumn[] = [
    { title: 'Instance', field: 'identifier' },
    { title: 'Status', field: 'status' },
    { title: 'Role', field: 'role' },
    { title: 'AZ', field: 'az' },
    { title: 'Class', field: 'instanceClass' },
  ];

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        className={classes.envSection}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center">
              <Typography variant="h6">{env.name}</Typography>
              {env.isDefault && <Chip label="Default" size="small" className={classes.defaultChip} />}
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 24 }} mr={2}>
              <Typography variant="body2" color="textSecondary">
                {env.accountId} | {env.region}
              </Typography>
              <Chip label={`${rdsCount} RDS`} size="small" variant="outlined" />
              <Chip label={`${eksCount} EKS`} size="small" variant="outlined" />
              {hasError ? <StatusWarning>Partial</StatusWarning> : summary ? <StatusOK>OK</StatusOK> : null}
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails className={classes.envDetails}>
          {loading && <Progress />}
          {error && <ResponseErrorPanel error={error} />}

          {!loading && !error && (
            <>
              {/* Active credentials */}
              {credentials && (
                <CredentialsPanel
                  credentials={credentials}
                  classes={classes}
                  fetchApi={fetchApi}
                  baseUrl={baseUrl}
                  onSnackbar={msg => setSnackbar({ open: true, message: msg, severity: 'success' })}
                />
              )}

              {accessError && (
                <Box mb={2}>
                  <Alert severity="error">{accessError}</Alert>
                </Box>
              )}

              {requesting && (
                <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={2}>
                  <CircularProgress size={20} />
                  <Typography>Creating temporary credentials...</Typography>
                </Box>
              )}

              {/* --- Quick Actions --- */}
              <Box className={classes.workflowActions}>
                <Typography className={classes.workflowActionsLabel}>Quick Actions:</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.deployBtn}
                  startIcon={<PublishIcon />}
                  disabled={!deployWorkflow || workflowsLoading}
                  onClick={() => { setActiveWorkflowAction('deploy'); setWorkflowDialogOpen(true); }}
                >
                  Deploy to {env.name}
                </Button>
                {workflowsLoading && <CircularProgress size={16} />}
              </Box>

              {/* --- RDS Section --- */}
              <Box className={classes.sectionTitle}>
                <StorageIcon className={classes.sectionIcon} />
                <Typography variant="h6">RDS Databases ({rdsClusters?.length ?? 0})</Typography>
              </Box>

              {rdsClusters && rdsClusters.length > 0 ? (
                rdsClusters.map((cluster: any) => {
                  const clusterId = cluster.DBClusterIdentifier;
                  const clusterInstances = rdsInstances.filter((i: any) => i.DBClusterIdentifier === clusterId);
                  const clusterMetrics = metrics[clusterId];
                  const knownDb = envDatabases.find(db => db.clusterIdentifier === clusterId);

                  return (
                    <Accordion key={clusterId} className={classes.clusterAccordion} TransitionProps={{ unmountOnExit: true }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box className={classes.clusterSummaryContent}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold', minWidth: 200 }}>
                            {clusterId}
                          </Typography>
                          <RdsStatusChip status={cluster.Status} />
                          <Typography variant="body2" color="textSecondary">
                            {cluster.Engine} {cluster.EngineVersion}
                          </Typography>
                          <Box className={classes.clusterMetric}>
                            <Typography variant="body2">
                              <strong>CPU:</strong> {clusterMetrics?.cpuUtilization ? `${parseFloat(clusterMetrics.cpuUtilization).toFixed(1)}%` : '—'}
                            </Typography>
                          </Box>
                          <Box className={classes.clusterMetric}>
                            <Typography variant="body2">
                              <strong>Conns:</strong> {clusterMetrics?.databaseConnections ?? '—'}
                            </Typography>
                          </Box>
                          {knownDb && <TunnelChip status={knownDb.tunnelStatus || 'unknown'} classes={classes} />}
                          {knownDb && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<LockOpenIcon />}
                              className={classes.requestBtn}
                              onClick={e => { e.stopPropagation(); setConfirmDb(knownDb); }}
                              disabled={requesting || knownDb.tunnelStatus !== 'connected'}
                            >
                              Request Access
                            </Button>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={classes.clusterDetails}>
                        {/* Connectivity */}
                        <Typography variant="subtitle2" gutterBottom>Connectivity</Typography>
                        <Box className={classes.detailRow}>
                          <span className={classes.label}>Writer:</span>
                          <span className={classes.mono}>{cluster.Endpoint || 'N/A'}</span>
                        </Box>
                        <Box className={classes.detailRow}>
                          <span className={classes.label}>Reader:</span>
                          <span className={classes.mono}>{cluster.ReaderEndpoint || 'N/A'}</span>
                        </Box>
                        <Box className={classes.detailRow}>
                          <span className={classes.label}>Port:</span>
                          <span className={classes.mono}>{cluster.Port}</span>
                        </Box>
                        {knownDb && (
                          <Box className={classes.detailRow}>
                            <span className={classes.label}>Proxy:</span>
                            <span className={classes.mono}>localhost:{knownDb.proxyPort}</span>
                          </Box>
                        )}

                        {/* Instances */}
                        {clusterInstances.length > 0 && (
                          <Box mt={2}>
                            <Table
                              title={`Instances (${clusterInstances.length})`}
                              columns={instanceColumns}
                              data={clusterInstances.map((i: any) => ({
                                identifier: i.DBInstanceIdentifier,
                                status: <RdsStatusChip status={i.DBInstanceStatus} />,
                                role: i.IsClusterWriter ? 'Writer' : 'Reader',
                                az: i.AvailabilityZone,
                                instanceClass: i.DBInstanceClass,
                              }))}
                              options={{ search: false, paging: false, toolbar: false, padding: 'dense' }}
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {rdsClusters === null ? 'Loading...' : 'No RDS clusters found'}
                </Typography>
              )}

              {/* --- EKS Section --- */}
              <Box className={classes.sectionTitle} style={{ marginTop: 32 }}>
                <CloudIcon className={classes.sectionIcon} />
                <Typography variant="h6">EKS Clusters ({eksClusters?.length ?? 0})</Typography>
              </Box>

              {eksClusters && eksClusters.length > 0 ? (
                eksClusters.map((cluster: any) => {
                  const nodeGroupCount = cluster.nodeGroups?.length || 0;
                  const totalDesired = cluster.totalDesiredNodes ?? 0;
                  const instanceTypes = cluster.nodeGroups?.map((ng: any) => ng.instanceTypes?.[0] || 'Unknown').filter((v: string, i: number, a: string[]) => a.indexOf(v) === i).join(', ') || 'N/A';

                  return (
                    <Accordion key={cluster.name} className={classes.clusterAccordion} TransitionProps={{ unmountOnExit: true }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box className={classes.clusterSummaryContent}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold', minWidth: 200 }}>
                            {cluster.name}
                          </Typography>
                          <EksStatusChip status={cluster.status} />
                          <Typography variant="body2" color="textSecondary">v{cluster.version}</Typography>
                          <Box className={classes.clusterMetric}>
                            <Typography variant="body2">
                              <strong>Groups:</strong> {nodeGroupCount}
                            </Typography>
                          </Box>
                          <Box className={classes.clusterMetric}>
                            <Typography variant="body2">
                              <strong>Nodes:</strong> {totalDesired}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">{instanceTypes}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={classes.clusterDetails}>
                        <EksClusterPanel
                          cluster={cluster}
                          envId={env.id}
                          baseUrl={baseUrl}
                          fetchApi={fetchApi}
                          onSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {eksClusters === null ? 'Loading...' : 'No EKS clusters found'}
                </Typography>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDb} onClose={() => setConfirmDb(null)}>
        <DialogTitle>Request Database Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create temporary <strong>read-only</strong> credentials for:
          </DialogContentText>
          <Box mt={1} mb={1}>
            <Typography variant="body2"><strong>Environment:</strong> {env.name}</Typography>
            <Typography variant="body2"><strong>Cluster:</strong> {confirmDb?.clusterIdentifier}</Typography>
            <Typography variant="body2"><strong>Database:</strong> {confirmDb?.databaseName}</Typography>
            <Typography variant="body2"><strong>App:</strong> {confirmDb?.appCode}</Typography>
          </Box>
          <DialogContentText>
            Credentials expire after <strong>24 hours</strong> with SELECT-only permissions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDb(null)}>Cancel</Button>
          <Button onClick={handleRequestAccess} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Workflow Dispatch Dialog */}
      <WorkflowDispatchDialog
        open={workflowDialogOpen}
        onClose={() => { setWorkflowDialogOpen(false); setActiveWorkflowAction(null); }}
        title={activeWorkflowAction === 'deploy'
          ? `Deploy All NexGen Services to ${env.name}`
          : 'Build All NexGen Services'}
        workflow={activeWorkflowAction === 'deploy' ? deployWorkflow : buildWorkflow}
        defaultBranch={workflowDefaultBranch}
        envId={env.id}
        workflowsApi={workflowsApi}
        onSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}>
        <Alert onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// --- Summary Card ---

const EnvironmentSummaryCard = ({
  env,
  summary,
  loading,
}: {
  env: Environment;
  summary: EnvSummary | undefined;
  loading: boolean;
}) => {
  const classes = useStyles();
  const hasError = summary?.rds?.error || summary?.eks?.error;

  return (
    <InfoCard
      title={env.name}
      subheader={`Account: ${env.accountId} | Region: ${env.region}`}
      className={classes.summaryCard}
    >
      {loading ? (
        <Progress />
      ) : (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box className={classes.metricBox}>
                <StorageIcon className={classes.metricIcon} />
                <Typography className={classes.metricValue}>
                  {summary?.rds?.error ? '—' : summary?.rds?.clusterCount ?? '—'}
                </Typography>
                <Typography variant="body2" className={classes.metricLabel}>RDS Clusters</Typography>
                {summary?.rds?.error && (
                  <Typography variant="caption" className={classes.errorText}>{summary.rds.error}</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className={classes.metricBox}>
                <CloudIcon className={classes.metricIcon} />
                <Typography className={classes.metricValue}>
                  {summary?.eks?.error ? '—' : summary?.eks?.clusterCount ?? '—'}
                </Typography>
                <Typography variant="body2" className={classes.metricLabel}>EKS Clusters</Typography>
                {summary?.eks?.error && (
                  <Typography variant="caption" className={classes.errorText}>{summary.eks.error}</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          <Box mt={2} textAlign="center">
            {hasError ? <StatusWarning>Some resources unavailable</StatusWarning> : <StatusOK>All resources accessible</StatusOK>}
          </Box>
        </Box>
      )}
    </InfoCard>
  );
};

// --- Main Page ---

export const EnvironmentsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const workflowsApi = useApi(githubWorkflowsApiRef);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [summaries, setSummaries] = useState<Record<string, EnvSummary>>({});
  const [knownDatabases, setKnownDatabases] = useState<KnownDatabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [baseUrl, setBaseUrl] = useState('');

  // Workflow state
  const [workflowsState, setWorkflowsState] = useState<{
    buildWorkflow: WorkflowItem | null;
    deployWorkflow: WorkflowItem | null;
    defaultBranch: string;
    loading: boolean;
  }>({ buildWorkflow: null, deployWorkflow: null, defaultBranch: 'main', loading: true });
  const [buildDialogOpen, setBuildDialogOpen] = useState(false);
  const [globalSnackbar, setGlobalSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [buildNotification, setBuildNotification] = useState<BuildNotification | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const proxyUrl = await discoveryApi.getBaseUrl('proxy');
        const base = `${proxyUrl}/api/aws`;
        setBaseUrl(base);

        // Fetch environments and known databases in parallel
        const [envsRes, dbRes] = await Promise.all([
          fetchApi.fetch(`${base}/envs`),
          fetchApi.fetch(`${base}/db/databases`).catch(() => null),
        ]);

        if (!envsRes.ok) throw new Error('Failed to fetch environments');
        const envs: Environment[] = await envsRes.json();
        setEnvironments(envs);

        // Parse known databases
        if (dbRes && dbRes.ok) {
          const dbData = await dbRes.json();
          setKnownDatabases(dbData.databases || []);
        }

        // Fetch summaries in parallel
        const summaryResults = await Promise.all(
          envs.map(env =>
            fetchApi.fetch(`${base}/envs/${env.id}/summary`)
              .then((r: Response) => r.json())
              .catch(() => ({
                envId: env.id,
                rds: { clusterCount: 0, error: 'Failed to connect' },
                eks: { clusterCount: 0, error: 'Failed to connect' },
              })),
          ),
        );
        const summaryMap: Record<string, EnvSummary> = {};
        summaryResults.forEach((s: EnvSummary) => { summaryMap[s.envId] = s; });
        setSummaries(summaryMap);
      } catch (e: any) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [discoveryApi, fetchApi]);

  // Fetch NexGen workflows (separate effect so env data loads independently)
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const branch = await workflowsApi.getBranchDefaultFromRepo(NEXGEN_HOSTNAME, NEXGEN_REPO);
        const workflows: WorkflowItem[] = await workflowsApi.listWorkflows(NEXGEN_HOSTNAME, NEXGEN_REPO, branch);
        const buildWf = workflows.find(w =>
          w.workflow.name.toLowerCase().includes(BUILD_WORKFLOW_MATCH),
        ) || null;
        const deployWf = workflows.find(w =>
          w.workflow.name.toLowerCase().includes(DEPLOY_WORKFLOW_MATCH),
        ) || null;
        setWorkflowsState({ buildWorkflow: buildWf, deployWorkflow: deployWf, defaultBranch: branch, loading: false });
      } catch (e) {
        console.warn('Failed to fetch NexGen workflows:', e);
        setWorkflowsState(prev => ({ ...prev, loading: false }));
      }
    };
    fetchWorkflows();
  }, [workflowsApi]);

  // Build completion monitoring
  const startBuildMonitoring = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const dispatchedAt = Date.now();
    setBuildNotification({
      phase: 'polling', runId: null, status: null, conclusion: null,
      htmlUrl: null, actor: null, createdAt: null, updatedAt: null, dispatchedAt,
    });

    const poll = async () => {
      try {
        const data = await (workflowsApi as any).listRunHistory(NEXGEN_HOSTNAME, NEXGEN_REPO, 'build all', 5);
        const runs: any[] = data.runs || [];
        // Match the run we just dispatched (created within 90s before/after dispatch)
        const ourRun = runs.find((r: any) => new Date(r.created_at).getTime() >= dispatchedAt - 90_000);
        if (ourRun) {
          const isComplete = ourRun.status === 'completed';
          setBuildNotification({
            phase: isComplete ? 'done' : 'running',
            runId: ourRun.id,
            status: ourRun.status,
            conclusion: ourRun.conclusion,
            htmlUrl: ourRun.html_url,
            actor: ourRun.actor?.login || null,
            createdAt: ourRun.created_at,
            updatedAt: ourRun.updated_at,
            dispatchedAt,
          });
          if (isComplete && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch { /* ignore poll errors silently */ }
    };

    // First poll after 8s (GitHub takes a moment to create the run), then every 20s
    const firstPollTimer = setTimeout(() => {
      poll();
      pollRef.current = setInterval(poll, 20_000);
    }, 8_000);

    // Store the timeout ID so we can clear it on unmount too
    return () => {
      clearTimeout(firstPollTimer);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  return (
    <Grid container spacing={3} direction="column">
      <Grid item>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
          <Box>
            <Typography variant="h4">Exoworks Environments</Typography>
            <Typography variant="body1" color="textSecondary">
              Multi-account resource overview — RDS databases, EKS clusters, and database access.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            className={classes.buildBtn}
            startIcon={<BuildIcon />}
            disabled={!workflowsState.buildWorkflow || workflowsState.loading}
            onClick={() => setBuildDialogOpen(true)}
          >
            Build All NexGen
          </Button>
        </Box>
        <WorkflowDispatchDialog
          open={buildDialogOpen}
          onClose={() => setBuildDialogOpen(false)}
          title="Build All NexGen Services"
          workflow={workflowsState.buildWorkflow}
          defaultBranch={workflowsState.defaultBranch}
          envId=""
          workflowsApi={workflowsApi}
          onSnackbar={(msg, severity) => setGlobalSnackbar({ open: true, message: msg, severity })}
          onDispatchSuccess={startBuildMonitoring}
        />
        <Snackbar open={globalSnackbar.open} autoHideDuration={4000} onClose={() => setGlobalSnackbar(s => ({ ...s, open: false }))}>
          <Alert onClose={() => setGlobalSnackbar(s => ({ ...s, open: false }))} severity={globalSnackbar.severity}>
            {globalSnackbar.message}
          </Alert>
        </Snackbar>
      </Grid>

      {/* Build Completion Popup — persists until manually dismissed */}
      {buildNotification && (
        <BuildCompletionPopup
          notification={buildNotification}
          onClose={() => {
            setBuildNotification(null);
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          }}
        />
      )}

      {/* Summary Cards */}
      <Grid item>
        <Grid container spacing={3}>
          {environments.map(env => (
            <Grid item xs={12} md={4} key={env.id}>
              <EnvironmentSummaryCard env={env} summary={summaries[env.id]} loading={!summaries[env.id]} />
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Database Dump */}
      {baseUrl && (
        <Grid item>
          <DatabaseDumpPanel baseUrl={baseUrl} fetchApi={fetchApi} />
        </Grid>
      )}

      {/* Environment Detail Sections */}
      <Grid item>
        {environments.map(env => (
          <EnvironmentSection
            key={env.id}
            env={env}
            summary={summaries[env.id]}
            knownDatabases={knownDatabases}
            baseUrl={baseUrl}
            fetchApi={fetchApi}
            identityApi={identityApi}
            buildWorkflow={workflowsState.buildWorkflow}
            deployWorkflow={workflowsState.deployWorkflow}
            workflowDefaultBranch={workflowsState.defaultBranch}
            workflowsApi={workflowsApi}
            workflowsLoading={workflowsState.loading}
          />
        ))}
      </Grid>
    </Grid>
  );
};
